/**
 * auth-service（BFF）的位址與各入口 URL 的組裝。
 *
 * 所有 app 共用同一個 auth-service，故位址集中在此，不由各 app 各自宣告。
 */

/**
 * BFF base URL。
 *
 * 以 `VITE_` 前綴讓值在**建置時**被 inline 進 bundle —— 這些 app 是靜態輸出，
 * 執行時沒有 server 可讀環境變數，故不能用執行時查表。
 *
 * ⚠️ **一定要走 `import.meta.env`，不能用 `process.env`。** Vite 不會替換其他
 * `process.env.*`，瀏覽器載入時會拋 `process is not defined` 導致畫面全白，
 * 而 build 本身不會報錯。
 *
 * 預設指向生產環境：本機開發也是連同一個 BFF（見 README 對本機限制的說明），
 * 需要指向他處時才覆寫，避免每個 app 都得配一份 .env。
 *
 * ⚠️ 前端與 BFF **不同源**，因此需要 CORS、CSRF cookie 設在共同父網域、以及
 * `return_to` 白名單。改動任一項前先讀 auth-service 的 README。
 */
const AUTH_BASE_URL = (
  import.meta.env.VITE_AUTH_BASE_URL ?? 'https://auth-api.codeincold.com'
).replace(/\/$/, '');

/**
 * 登入完成後要回到哪裡。
 *
 * 取當前分頁的 origin，而非寫死網域 —— 同一份程式碼會跑在 diet、square-photo
 * 等不同子網域，寫死的話其中一邊必定導錯。
 *
 * ⚠️ auth-service 會以白名單（`bff.allowed-origins`）驗證這個值，Keycloak 那側
 *    也有 `valid_post_logout_redirect_uris`。**新增 app 時兩處都要加**，
 *    否則會靜默退回預設網域，看起來像「登入後跳到別的 app」。
 */
function currentOrigin(): string {
  /* 靜態輸出的 HTML 產於建置時，此時無 window；呼叫端一律在事件處理中才用到 */
  return typeof window === 'undefined' ? '' : window.location.origin;
}

/** 目前登入者的資訊端點。 */
export function meUrl(): string {
  return `${AUTH_BASE_URL}/api/me`;
}

/**
 * 經 BFF 代理的後端 API 位址。
 *
 * 領域服務（diet-service 等）都在 api.codeincold.com，但前端不直接打那裡 ——
 * 那需要 access token，而 token 刻意留在 BFF 後端。故一律經由本服務轉發。
 */
export function apiUrl(path: string): string {
  return `${AUTH_BASE_URL}${path}`;
}

/** 登出端點（POST，需帶 CSRF token）。 */
export function logoutUrl(): string {
  return `${AUTH_BASE_URL}/logout`;
}

/**
 * 登入入口。
 *
 * 這是**整頁導向**而非 fetch：OIDC 的 Authorization Code 流程需要瀏覽器實際走一趟
 * Keycloak，使用者在那裡輸入密碼。密碼永遠不經過本前端 —— 這正是 BFF 的重點，
 * 也是為什麼這裡沒有、也不該有帳號密碼表單。
 */
export function loginUrl(): string {
  const returnTo = encodeURIComponent(currentOrigin());
  return `${AUTH_BASE_URL}/oauth2/authorization/keycloak?return_to=${returnTo}`;
}

/**
 * 註冊入口：同一個授權流程，只是讓 Keycloak 先顯示註冊頁。
 *
 * ⚠️ 參數名是 `screen` 而非 `action` —— 後者會被 Spring Security 拿去展開
 *    redirect-uri 模板，導致 redirect_uri 變成 /register/oauth2/code/keycloak
 *    而被 Keycloak 拒絕。
 */
export function registerUrl(): string {
  return `${loginUrl()}&screen=register`;
}

/**
 * 用 Google 繼續。
 *
 * 同一條 OIDC 授權碼流程，只多送一個 `idp` 參數：auth-service 會把它轉成 Keycloak 的
 * `kc_idp_hint`，Keycloak 便略過自家登入頁、直接把使用者送去 Google。前端與 Google
 * 之間沒有任何直接往來 —— 不載入 Google 的 SDK、拿不到 Google 的 token，
 * 這條路徑與密碼登入的差別僅止於使用者在 Keycloak 後面看到誰的畫面。
 *
 * **不需要對應的「用 Google 註冊」。** 首次以 Google 登入時 Keycloak 會自動建立帳號，
 * 註冊與登入是同一個入口，做成兩顆按鈕只會讓人以為選錯了會出事。
 *
 * ⚠️ `idp` 的值必須與 Keycloak 那側的 identity provider alias 一致（SRE repo 的
 *    `keycloak_oidc_google_identity_provider.alias`），且需列在 auth-service 的
 *    `ALLOWED_IDP_ALIASES` 白名單裡，否則參數會被丟掉、按鈕看起來像是沒反應。
 */
export function googleLoginUrl(): string {
  return `${loginUrl()}&idp=google`;
}

/** 登出後導回的位址，交由 auth-service 依白名單驗證。 */
export function returnToParam(): string {
  return currentOrigin();
}
